import { roundAddressFromPathname } from "../earlyRoundFetches";

/**
 * The page family a location belongs to, for presence segments: a short
 * label that groups routes the way a person would name where they were
 * ("Funder › Understand", "Proposal", "Admin › Usage"), never a raw path.
 * Ids stay out of the label so that a hundred proposals are one page family
 * and the label vocabulary stays small enough to rank. The round reference
 * is kept beside the label for pages inside a round.
 *
 * Pure so both the client (classifying at segment open) and the read side
 * (tests, future re-labelling) share one vocabulary. `PAGE_LABEL_MAX_LENGTH`
 * bounds what the write route accepts.
 */
export const PAGE_LABEL_MAX_LENGTH = 64;
/** A round reference is a round id or a valid custom address, as the app's
 *  own URL parser decides (`roundAddressFromPathname`); the write route's
 *  bound only has to admit what that parser can return (an address is at
 *  most 80 characters, an id far shorter). */
export const ROUND_REF_MAX_LENGTH = 80;

export interface PageFamily {
  page: string;
  roundRef: string | null;
}

// The live tabs are Review and Decide (`Round.tsx`); `understand` and
// `materials` are redirects onto Review kept for old bookmarks, labelled as
// their destination so a redirect does not flap a segment through a label
// nobody uses.
const FUNDER_TABS: Record<string, string> = {
  review: "Funder › Review",
  decide: "Funder › Decide",
  understand: "Funder › Review",
  materials: "Funder › Review",
};

// Every first path segment `Round.tsx` routes, by name. The test pins this
// table against the router, so a new round page cannot land in "Other"
// unnoticed.
const ROUND_PAGES: Record<string, string> = {
  "": "Round home",
  proposals: "Proposals",
  comparisons: "Comparisons",
  disagreements: "Disagreements",
  "funding-flow": "Funding flow",
  investigations: "Investigations",
  recusals: "Recusals",
  members: "Members",
  onboarding: "Onboarding",
  applications: "Applications",
  "speculation-grants": "Speculation grants",
  "export-data": "Export data",
  settings: "Round settings",
  admin: "Round admin",
};

// Every first path segment `App.tsx` routes outside a round and the admin
// tools, by name (the test pins this against the router too).
const TOP_PAGES: Record<string, string> = {
  "": "Home",
  settings: "Settings",
  teams: "Teams",
  "team-invitations": "Teams",
  "application-builder": "Application builder",
  applications: "Saved application",
  apply: "Apply",
  applicant: "Applicant",
  ops: "Ops",
  "due-diligence": "Due Diligence",
  dev: "Dev",
  signout: "Other",
  privacy: "Privacy policy",
  resources: "Resources",
};

// The admin tools, by their first path segment. A tool absent here reads
// as "Admin › Other" until it is added: labels are a fixed vocabulary, never
// text copied from the URL, so a pasted or mistyped address can leak nothing
// into a stored row.
const ADMIN_TOOLS: Record<string, string> = {
  applications: "Admin › Applications",
  "application-builder": "Admin › Application builder",
  teams: "Admin › Teams",
  users: "Admin › Users",
  usage: "Admin › Usage",
  emails: "Admin › Emails",
  notifications: "Admin › Notifications",
  "scheduled-jobs": "Admin › Scheduled jobs",
  pipelines: "Admin › Pipelines",
  "round-preparedness": "Admin › Round preparedness",
  "feature-flags": "Admin › Feature flags",
  pangram: "Admin › Pangram",
  "llm-audit": "Admin › LLM audit",
  "agent-tokens": "Admin › Agent tokens",
  "sheet-imports": "Admin › Sheet imports",
  health: "Admin › Health",
  "bug-hunt-runs": "Admin › Bug hunt runs",
  operations: "Admin › Operations",
  performance: "Admin › Performance",
  "funder-ratings": "Admin › Funder ratings",
  "funder-allocations": "Admin › Funder allocations",
  "funder-todo": "Admin › Funder to do",
  "notes-analysis": "Admin › Notes analysis",
  "note-history": "Admin › Note history",
  "evaluator-scoring": "Admin › Evaluator scoring",
  "evaluator-analysis": "Admin › Evaluator analysis",
  "offline-prompt": "Admin › Offline prompt",
  transcripts: "Admin › Transcripts",
  videos: "Admin › Videos",
  resources: "Admin › Resources",
  "llm-extractions": "Admin › LLM extractions",
  "mvf-quality": "Admin › MVF quality",
  "fact-checking": "Admin › Fact checking",
  informedness: "Admin › Informedness",
  "prompt-lab": "Admin › Prompt lab",
  "proposal-geography": "Admin › Proposal geography",
  "proposal-summaries": "Admin › Proposal summaries",
  "proposal-application-summary": "Admin › Proposal application summary",
  "proposal-deep-research": "Admin › Proposal deep research",
  "proposal-gdpr-status": "Admin › Proposal GDPR status",
  "recommender-summaries": "Admin › Recommender summaries",
  "recommender-notes": "Admin › Recommender notes",
  "track-summaries": "Admin › Track summaries",
  "sentiment-analysis": "Admin › Sentiment analysis",
  "key-people-extraction": "Admin › Key people extraction",
  "key-people-deep-research": "Admin › Key people deep research",
  "key-individual-gdpr-status": "Admin › Key individual GDPR status",
  "starting-weights": "Admin › Starting weights",
  "past-proposal-matching": "Admin › Past proposal matching",
  "note-scoring-lab": "Admin › Note scoring lab",
  "llm-note-scoring-calibration": "Admin › LLM note scoring calibration",
  "speculator-discussion": "Admin › Speculator discussion",
};

/** A lookup that never falls through to the prototype: `/constructor` is
 *  a path someone can type. */
function lookup(table: Record<string, string>, key: string): string | null {
  return Object.hasOwn(table, key) ? table[key]! : null;
}

/** The route vocabulary the tests pin against the routers. */
export const PAGE_TABLES = {
  top: TOP_PAGES,
  round: ROUND_PAGES,
  admin: ADMIN_TOOLS,
  funderTabs: FUNDER_TABS,
} as const;

/**
 * The iPhone shell navigates an in-memory stack, not the browser history,
 * with its own three screens; its paths are classified here so they never
 * collide with the web shell's ("/" is the shell's round screen, not Home).
 * Every screen is inside the shell's one round, which the caller names.
 */
export function iphonePageFamilyFor(
  path: string,
  roundRef: string | null = null,
): PageFamily {
  const [first = ""] = path.split("?")[0]!.split("/").filter(Boolean);

  if (first === "") {
    return { page: "iPhone › Round", roundRef };
  }

  if (first === "queue") {
    return { page: "iPhone › Queue", roundRef };
  }

  if (first === "video") {
    return { page: "iPhone › Video", roundRef };
  }

  return { page: "iPhone › Other", roundRef };
}

export function pageFamilyFor(pathname: string): PageFamily {
  const parts = pathname
    .split("?")[0]!
    .split("#")[0]!
    .split("/")
    .filter(Boolean);
  const [first = "", second = "", third = ""] = parts;

  if (first === "round" && second !== "") {
    // The app's own parser decides what a round reference is (an id, or a
    // valid custom address); anything else — a mistyped or pasted path —
    // keeps the page but stores no reference. The parser bounds an address
    // but not an id, and the write route refuses anything longer than the
    // column allows — which would drop the whole segment from the outbox —
    // so an over-long id is dropped here as well.
    const parsed = roundAddressFromPathname(`/round/${second}`);
    const roundRef =
      parsed != null && parsed.length <= ROUND_REF_MAX_LENGTH ? parsed : null;

    if (third === "funder") {
      const tab = parts[3] ?? "";
      return { page: lookup(FUNDER_TABS, tab) ?? "Funder", roundRef };
    }

    if (third === "proposals" && parts[3] !== undefined) {
      return { page: "Proposal", roundRef };
    }

    return { page: lookup(ROUND_PAGES, third) ?? "Round › Other", roundRef };
  }

  if (first === "ops" && second === "due-diligence") {
    return { page: "Due Diligence", roundRef: null };
  }

  if (first === "admin") {
    return {
      page:
        second === ""
          ? "Admin tools"
          : (lookup(ADMIN_TOOLS, second) ?? "Admin › Other"),
      roundRef: null,
    };
  }

  return { page: lookup(TOP_PAGES, first) ?? "Other", roundRef: null };
}
